import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixPortfolioProjectsV1Project from './portfolio-projects-v1-project-projects.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformSDKVideoV2ToRESTVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { transformSDKPageURLV2ToRESTPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface Project extends ProjectCoverOneOf {
  /** Project cover image. */
  coverImage?: Image;
  /** Project cover video. */
  coverVideo?: Video;
  /**
   * Project ID.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.
   * @readonly
   */
  revision?: string | null;
  /**
   * Project title.
   * @maxLength 100
   */
  title?: string | null;
  /** Project description. */
  description?: string | null;
  /** Whether the project is hidden from the portfolio. Default: `false` */
  hidden?: boolean | null;
  /**
   * IDs of the collections that include the project.
   * @format GUID
   * @maxSize 1000
   */
  collectionIds?: string[];
  /** Project details. */
  details?: ProjectDetail[];
  /** Project slug. */
  slug?: string | null;
  /**
   * Date and time the project was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the project was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.
   * @readonly
   */
  url?: string;
  /** Project SEO data. */
  seoData?: SeoSchema;
  /** Optional watermark that can be applied to all project's images. */
  watermark?: Watermark;
}

/** @oneof */
export interface ProjectCoverOneOf {
  /** Project cover image. */
  coverImage?: Image;
  /** Project cover video. */
  coverVideo?: Video;
}

export interface Image {
  /** Information about the Wix Media image. */
  imageInfo?: string;
  /** Focal point of the image. */
  focalPoint?: Point;
}

export enum ImageType {
  UNDEFINED = 'UNDEFINED',
  WIX_MEDIA = 'WIX_MEDIA',
  EXTERNAL = 'EXTERNAL',
}

/** @enumType */
export type ImageTypeWithLiterals =
  | ImageType
  | 'UNDEFINED'
  | 'WIX_MEDIA'
  | 'EXTERNAL';

export interface Point {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
}

export interface UnsharpMasking {
  /**
   * Unsharp masking amount. Controls the sharpening strength. <br />
   *
   * Min: `0` <br />
   * Max: `5`
   * @max 5
   */
  amount?: number | null;
  /** Unsharp masking radius in pixels. Controls the sharpening width. */
  radius?: number | null;
  /**
   * Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />
   *
   * Min: `0` <br />
   * Max: `1`
   * @max 1
   */
  threshold?: number | null;
}

export interface Video {
  /** Information about the Wix Media video. */
  videoInfo?: string;
  /** Manually defined Video duration in milliseconds. */
  durationInMillis?: number | null;
}

export interface VideoResolution {
  /**
   * Video URL.  Required.
   * @maxLength 8000
   */
  url?: string;
  /** Video height. Required. */
  height?: number;
  /** Video width.  Required. */
  width?: number;
  /**
   * Video format for example, mp4, hls.  Required.
   * @maxLength 50
   */
  format?: string;
  /**
   * Video quality for example 480p, 720p.
   * @maxLength 50
   */
  quality?: string | null;
  /**
   * Video filename.
   * @maxLength 512
   */
  filename?: string | null;
}

/**
 * Project label.
 * One of:
 * + `text`
 * + `link`
 */
export interface ProjectDetail extends ProjectDetailValueOneOf {
  /** Project label in plain text format. */
  text?: string;
  /** Project label in link format. */
  link?: DetailsLink;
  /** Project label. */
  label?: string;
}

/** @oneof */
export interface ProjectDetailValueOneOf {
  /** Project label in plain text format. */
  text?: string;
  /** Project label in link format. */
  link?: DetailsLink;
}

export interface DetailsLink {
  /** Display text of the link. */
  text?: string | null;
  /**
   * Target URL of the link.
   * @format WEB_URL
   */
  url?: string | null;
  /**
   * Whether the link opens in a new tab or window. One of:
   * * `'_blank'`: The link opens in a new tab or window.
   * * `'_self'`: The link opens in the same tab or window.
   */
  target?: string | null;
}

export interface ProjectSource {
  /**
   * App definition id
   * @immutable
   */
  appDefId?: string;
  /**
   * External source id
   * @immutable
   */
  externalId?: string;
  /**
   * Source name
   * @maxLength 100
   * @immutable
   */
  sourceName?: string;
  /**
   * Source description
   * @immutable
   */
  description?: string | null;
  /**
   * link to external source
   * @format WEB_URL
   */
  link?: string | null;
  /** Sync status */
  syncStatus?: SyncStatusWithLiterals;
  /** fields that are synced from external source, should be blocked to update in UI */
  notEditableFields?: string[];
  /** last date the project was synced */
  lastSync?: Date | null;
}

export enum SyncStatus {
  SYNCED = 'SYNCED',
  SYNCING = 'SYNCING',
  NOT_SYNCED = 'NOT_SYNCED',
}

/** @enumType */
export type SyncStatusWithLiterals =
  | SyncStatus
  | 'SYNCED'
  | 'SYNCING'
  | 'NOT_SYNCED';

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /** Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages). */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

/** Optional Watermark that can be applied to all project's images */
export interface Watermark {
  /** Position of the watermark on the image. */
  position?: PositionWithLiterals;
  /**
   * Size of the watermark, from 0 to 100.
   * @max 100
   */
  size?: number;
  /**
   * Opacity of the watermark.
   * @min 1
   * @max 100
   */
  opacity?: number;
  /**
   * Image ID of the Watermark as saved in Media Platform.
   * @maxLength 300
   */
  imageUrl?: string;
  /** Whether the watermark is to be displayed on Items & Cover Photos. */
  enabled?: boolean;
}

export enum Position {
  NORTH_WEST = 'NORTH_WEST',
  NORTH = 'NORTH',
  NORTH_EAST = 'NORTH_EAST',
  WEST = 'WEST',
  CENTER = 'CENTER',
  EAST = 'EAST',
  SOUTH_WEST = 'SOUTH_WEST',
  SOUTH = 'SOUTH',
  SOUTH_EAST = 'SOUTH_EAST',
}

/** @enumType */
export type PositionWithLiterals =
  | Position
  | 'NORTH_WEST'
  | 'NORTH'
  | 'NORTH_EAST'
  | 'WEST'
  | 'CENTER'
  | 'EAST'
  | 'SOUTH_WEST'
  | 'SOUTH'
  | 'SOUTH_EAST';

export interface MenuSettingUpdatedEvent {}

export interface GetProjectPageDataRequest {
  /** Slug of the project's current collection */
  collectionSlug?: string;
  /** Project's slug */
  projectSlug?: string;
}

export interface GetProjectPageDataResponse {
  /** Project data */
  project?: Project;
  /** Previous project slug */
  previousProject?: ProjectSlug;
  /** Next project slug */
  nextProject?: ProjectSlug;
}

export interface ProjectSlug {
  /**
   * Project id
   * @format GUID
   */
  projectId?: string;
  /** Project slug */
  slug?: string;
}

export interface CreateNewPortfolioAppRequest {}

export interface CreateNewPortfolioAppResponse {}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /** AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
  /** Nautilus platform. */
  NAUTILUS = 'NAUTILUS',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS'
  | 'NAUTILUS';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface SyncProjectWithCollectionMappings {
  /**
   * Id of recently updated Project
   * @format GUID
   */
  projectId?: string;
  /**
   * fields that were updated in the given project
   * @maxSize 100
   */
  fieldMaskPaths?: string[];
}

export interface DeletedProjectRestored {
  /**
   * the id of the project that was restored
   * @format GUID
   */
  projectId?: string;
  /** timestamp for when the project was originally deleted. */
  deletionTimestamp?: Date | null;
}

export interface CreateProjectRequest {
  /** Project to create. */
  project: Project;
}

export interface CreateProjectResponse {
  /** Created project. */
  project?: Project;
}

export interface GetProjectRequest {
  /**
   * ID of the project to retrieve.
   * @format GUID
   */
  projectId: string;
  /** Whether to include the project's relative path and full URL in the response. Default: `false` */
  includePageUrl?: boolean | null;
}

export interface GetProjectResponse {
  /** The requested project. */
  project?: Project;
}

export interface ListProjectsRequest {
  /** Projects limit per response is maximum 100, In the first request the cursor is None ? */
  paging?: CursorPaging;
  /** Whether to include the project's relative path and full URL in the response. Default: `false` */
  includePageUrl?: boolean | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface ListProjectsResponse {
  /** List of projects. */
  projects?: Project[];
  /** Paging metadata. */
  metadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateProjectRequest {
  /** Project to update. */
  project: Project;
}

export interface UpdateProjectResponse {
  /** Updated project. */
  project?: Project;
}

export interface BulkUpdateProjectsRequest {
  /**
   * Projects to update.
   * @maxSize 100
   */
  projects?: MaskedProject[];
  /**
   * Whether to return the updated projects.
   *
   * Set to `true` to return the projects in the response.
   *
   * Default: `false`
   */
  returnFullEntity?: boolean | null;
}

export interface MaskedProject {
  /** Project to update. */
  project?: Project;
}

export interface BulkUpdateProjectsResponse {
  /** List of individual Bulk Update Projects results. */
  results?: BulkUpdateProjectsResult[];
  /** Total number of successes and failures for Bulk Update Projects. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateProjectsResult {
  /**
   * Information about the updated project.
   * Including its ID, index in the bulk request and whether it was successfully updated.
   */
  itemMetadata?: ItemMetadata;
  /** Updated project. Only returned if `returnEntity` is set to `true` in the request. */
  project?: Project;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface DeleteProjectRequest {
  /**
   * ID of the project to delete.
   * @format GUID
   */
  projectId: string;
}

export interface DeleteProjectResponse {
  /**
   * ID of the deleted project.
   * @format GUID
   */
  projectId?: string;
}

export interface QueryProjectsRequest {
  /** Query options. */
  query: QueryV2;
  /** Whether to include the project's relative path and full URL in the response. Default: `false` */
  includePageUrl?: boolean | null;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryProjectsResponse {
  /** List of projects. */
  projects?: Project[];
  /** Paging metadata. */
  metadata?: PagingMetadataV2;
}

export interface UpdateProjectOrderInCollectionRequest {
  /**
   * ID of the project to update.
   * @format GUID
   */
  projectId: string;
  /**
   * Collection ID.
   * @format GUID
   */
  collectionId: string;
  /** Index that determines the placement of a project within the collection. */
  sortOrder: number | null;
}

export interface UpdateProjectOrderInCollectionResponse {
  /** Updated project placement within the specified collection. */
  projectInCollection?: ProjectsInCollections;
}

export interface ProjectsInCollections {
  /**
   * Collection ID.
   * @format GUID
   */
  collectionId?: string;
  /** Project object. */
  project?: Project;
  /**
   * Index that determines the placement of a project within the collection.  <br />
   *
   * Default: [Epoch](https://www.epoch101.com/) timestamp. <br />
   */
  sortOrder?: number | null;
  /**
   * Project placement ID.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
}

export interface QueryProjectWithCollectionInfoRequest {
  /** WQL expression */
  query: QueryV2;
  /** Include page url */
  includePageUrl?: boolean | null;
}

export interface QueryProjectWithCollectionInfoResponse {
  /** The retrieved Projects in Collection */
  projects?: ProjectsInCollections[];
  /** Paging metadata */
  metadata?: PagingMetadataV2;
}

export interface RestoreProjectFromTrashBinRequest {
  /**
   * id of deleted project to restore from trash bin.
   * @format GUID
   */
  projectId?: string;
}

export interface RestoreProjectFromTrashBinResponse {
  /** project that was restored from trash bin */
  project?: Project;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface GetAdjacentProjectInfoRequest {
  /**
   * Collection ID to search within
   * @format GUID
   */
  collectionId?: string;
  /** Current project's sort order for finding adjacent projects */
  currentSortOrder?: number;
}

export interface GetAdjacentProjectInfoResponse {
  /** Previous project info (optional) */
  previousProject?: ProjectInfo;
  /** Next project info (optional) */
  nextProject?: ProjectInfo;
}

export interface ProjectInfo {
  /**
   * Project ID
   * @format GUID
   */
  projectId?: string;
  /**
   * Project slug
   * @maxLength 256
   */
  slug?: string;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ProjectCreatedEnvelope {
  entity: Project;
  metadata: EventMetadata;
}

/**
 * Triggered when a project is created.
 * @permissionScope Manage Portfolio
 * @permissionScopeId SCOPE.PORTFOLIO.MANAGE-PORTFOLIO
 * @permissionId PORTFOLIO.PROJECT_READ
 * @webhook
 * @eventType wix.portfolio.projects.v1.project_created
 * @serviceIdentifier com.wixpress.portfolio.projects.ProjectsService
 * @slug created
 */
export declare function onProjectCreated(
  handler: (event: ProjectCreatedEnvelope) => void | Promise<void>
): void;

export interface ProjectDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a project is deleted.
 * @permissionScope Manage Portfolio
 * @permissionScopeId SCOPE.PORTFOLIO.MANAGE-PORTFOLIO
 * @permissionId PORTFOLIO.PROJECT_READ
 * @webhook
 * @eventType wix.portfolio.projects.v1.project_deleted
 * @serviceIdentifier com.wixpress.portfolio.projects.ProjectsService
 * @slug deleted
 */
export declare function onProjectDeleted(
  handler: (event: ProjectDeletedEnvelope) => void | Promise<void>
): void;

export interface ProjectUpdatedEnvelope {
  entity: Project;
  metadata: EventMetadata;
}

/**
 * Triggered when a project is updated.
 * @permissionScope Manage Portfolio
 * @permissionScopeId SCOPE.PORTFOLIO.MANAGE-PORTFOLIO
 * @permissionId PORTFOLIO.PROJECT_READ
 * @webhook
 * @eventType wix.portfolio.projects.v1.project_updated
 * @serviceIdentifier com.wixpress.portfolio.projects.ProjectsService
 * @slug updated
 */
export declare function onProjectUpdated(
  handler: (event: ProjectUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a project.
 * @param project - Project to create.
 * @public
 * @requiredField project
 * @permissionId PORTFOLIO.PROJECT_CREATE
 * @applicableIdentity APP
 * @returns Created project.
 * @fqn com.wixpress.portfolio.projects.ProjectsService.CreateProject
 */
export async function createProject(
  project: Project
): Promise<
  NonNullablePaths<
    Project,
    | `coverImage.focalPoint.x`
    | `coverImage.focalPoint.y`
    | `collectionIds`
    | `details`
    | `details.${number}.text`
    | `details.${number}.label`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`
    | `watermark.position`
    | `watermark.size`
    | `watermark.opacity`
    | `watermark.imageUrl`
    | `watermark.enabled`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ project: project }),
    [
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [{ path: 'project.url' }],
      },
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'project.coverImage.imageInfo' }],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'project.coverVideo.videoInfo' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixPortfolioProjectsV1Project.createProject(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'project.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'project.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'project.coverVideo.videoInfo' }],
        },
      ])
    )?.project!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { project: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['project']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a project.
 * @param projectId - ID of the project to retrieve.
 * @public
 * @requiredField projectId
 * @permissionId PORTFOLIO.PROJECT_READ
 * @applicableIdentity APP
 * @returns The requested project.
 * @fqn com.wixpress.portfolio.projects.ProjectsService.GetProject
 */
export async function getProject(
  projectId: string,
  options?: GetProjectOptions
): Promise<
  NonNullablePaths<
    Project,
    | `coverImage.focalPoint.x`
    | `coverImage.focalPoint.y`
    | `collectionIds`
    | `details`
    | `details.${number}.text`
    | `details.${number}.label`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`
    | `watermark.position`
    | `watermark.size`
    | `watermark.opacity`
    | `watermark.imageUrl`
    | `watermark.enabled`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    projectId: projectId,
    includePageUrl: options?.includePageUrl,
  });

  const reqOpts = ambassadorWixPortfolioProjectsV1Project.getProject(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'project.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'project.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'project.coverVideo.videoInfo' }],
        },
      ])
    )?.project!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          projectId: '$[0]',
          includePageUrl: '$[1].includePageUrl',
        },
        singleArgumentUnchanged: false,
      },
      ['projectId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetProjectOptions {
  /** Whether to include the project's relative path and full URL in the response. Default: `false` */
  includePageUrl?: boolean | null;
}

/**
 * Retrieves a list of all projects in a portfolio.
 * @public
 * @permissionId PORTFOLIO.PROJECT_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.portfolio.projects.ProjectsService.ListProjects
 */
export async function listProjects(
  options?: ListProjectsOptions
): Promise<
  NonNullablePaths<
    ListProjectsResponse,
    | `projects`
    | `projects.${number}.coverImage.focalPoint.x`
    | `projects.${number}.coverImage.focalPoint.y`
    | `projects.${number}.seoData.settings.preventAutoRedirect`
    | `projects.${number}.watermark.position`
    | `projects.${number}.watermark.size`
    | `projects.${number}.watermark.opacity`
    | `projects.${number}.watermark.imageUrl`
    | `projects.${number}.watermark.enabled`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paging: options?.paging,
    includePageUrl: options?.includePageUrl,
  });

  const reqOpts = ambassadorWixPortfolioProjectsV1Project.listProjects(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'projects.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'projects.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'projects.coverVideo.videoInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          paging: '$[0].paging',
          includePageUrl: '$[0].includePageUrl',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListProjectsOptions {
  /** Projects limit per response is maximum 100, In the first request the cursor is None ? */
  paging?: CursorPaging;
  /** Whether to include the project's relative path and full URL in the response. Default: `false` */
  includePageUrl?: boolean | null;
}

/**
 * Updates a project.
 * @param _id - Project ID.
 * @public
 * @requiredField _id
 * @requiredField project
 * @requiredField project.revision
 * @permissionId PORTFOLIO.PROJECT_UPDATE
 * @applicableIdentity APP
 * @returns Updated project.
 * @fqn com.wixpress.portfolio.projects.ProjectsService.UpdateProject
 */
export async function updateProject(
  _id: string,
  project: NonNullablePaths<UpdateProject, `revision`, 2>
): Promise<
  NonNullablePaths<
    Project,
    | `coverImage.focalPoint.x`
    | `coverImage.focalPoint.y`
    | `collectionIds`
    | `details`
    | `details.${number}.text`
    | `details.${number}.label`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`
    | `watermark.position`
    | `watermark.size`
    | `watermark.opacity`
    | `watermark.imageUrl`
    | `watermark.enabled`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ project: { ...project, id: _id } }),
    [
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [{ path: 'project.url' }],
      },
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'project.coverImage.imageInfo' }],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'project.coverVideo.videoInfo' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixPortfolioProjectsV1Project.updateProject(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'project.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'project.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'project.coverVideo.videoInfo' }],
        },
      ])
    )?.project!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { project: '$[1]' },
        explicitPathsToArguments: { 'project.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'project']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateProject {
  /** Project cover image. */
  coverImage?: Image;
  /** Project cover video. */
  coverVideo?: Video;
  /**
   * Project ID.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.
   * @readonly
   */
  revision?: string | null;
  /**
   * Project title.
   * @maxLength 100
   */
  title?: string | null;
  /** Project description. */
  description?: string | null;
  /** Whether the project is hidden from the portfolio. Default: `false` */
  hidden?: boolean | null;
  /**
   * IDs of the collections that include the project.
   * @format GUID
   * @maxSize 1000
   */
  collectionIds?: string[];
  /** Project details. */
  details?: ProjectDetail[];
  /** Project slug. */
  slug?: string | null;
  /**
   * Date and time the project was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the project was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.
   * @readonly
   */
  url?: string;
  /** Project SEO data. */
  seoData?: SeoSchema;
  /** Optional watermark that can be applied to all project's images. */
  watermark?: Watermark;
}

/**
 * Updates multiple projects.
 *
 * To update a single project, call Update Project.
 * @public
 * @requiredField options.projects.project
 * @requiredField options.projects.project._id
 * @requiredField options.projects.project.revision
 * @permissionId PORTFOLIO.PROJECT_UPDATE
 * @applicableIdentity APP
 * @fqn com.wixpress.portfolio.projects.ProjectsService.BulkUpdateProjects
 */
export async function bulkUpdateProjects(
  options?: NonNullablePaths<
    BulkUpdateProjectsOptions,
    | `projects.${number}.project`
    | `projects.${number}.project._id`
    | `projects.${number}.project.revision`,
    5
  >
): Promise<
  NonNullablePaths<
    BulkUpdateProjectsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.project.watermark.position`
    | `results.${number}.project.watermark.size`
    | `results.${number}.project.watermark.opacity`
    | `results.${number}.project.watermark.imageUrl`
    | `results.${number}.project.watermark.enabled`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      projects: options?.projects,
      returnFullEntity: options?.returnFullEntity,
    }),
    [
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [{ path: 'projects.project.url' }],
      },
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'projects.project.coverImage.imageInfo' }],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'projects.project.coverVideo.videoInfo' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixPortfolioProjectsV1Project.bulkUpdateProjects(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'results.project.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'results.project.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'results.project.coverVideo.videoInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          projects: '$[0].projects',
          returnFullEntity: '$[0].returnFullEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateProjectsOptions {
  /**
   * Projects to update.
   * @maxSize 100
   */
  projects?: MaskedProject[];
  /**
   * Whether to return the updated projects.
   *
   * Set to `true` to return the projects in the response.
   *
   * Default: `false`
   */
  returnFullEntity?: boolean | null;
}

/**
 * Deletes a project.
 * @param projectId - ID of the project to delete.
 * @public
 * @requiredField projectId
 * @permissionId PORTFOLIO.PROJECT_DELETE
 * @applicableIdentity APP
 * @fqn com.wixpress.portfolio.projects.ProjectsService.DeleteProject
 */
export async function deleteProject(
  projectId: string
): Promise<NonNullablePaths<DeleteProjectResponse, `projectId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    projectId: projectId,
  });

  const reqOpts =
    ambassadorWixPortfolioProjectsV1Project.deleteProject(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { projectId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['projectId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of projects.
 *
 * The `queryProjects()` method builds a query to retrieve a list of projects and returns a `ProjectsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-builder/find) method.
 *
 * You can refine the query by chaining `ProjectsQueryBuilder` methods onto the query. `ProjectsQueryBuilder` methods enable you to filter, sort, and control the results that `queryProjects()` returns.
 *
 * `queryProjects()`  runs with the following `ProjectsQueryBuilder` defaults which you can override:
 *
 * + [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-builder/limit)
 * + [`ascending('_id')`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-builder/ascending)
 *
 * The following `ProjectsQueryBuilder` methods are supported for `queryProjects()`. For a full description of the operations object, see the object returned for the [`items`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/projects/projects-query-result/items) property in `ProjectsQueryResult`.
 * @public
 * @permissionId PORTFOLIO.PROJECT_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.portfolio.projects.ProjectsService.QueryProjects
 */
export function queryProjects(
  options?: QueryProjectsOptions
): ProjectsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Project,
    'CURSOR',
    QueryProjectsRequest,
    QueryProjectsResponse
  >({
    func: async (payload: QueryProjectsRequest) => {
      const reqOpts = ambassadorWixPortfolioProjectsV1Project.queryProjects({
        ...payload,
        ...(options ?? {}),
      });

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryProjectsRequest['query']) => {
      const args = [query, options] as [
        QueryProjectsRequest['query'],
        QueryProjectsOptions
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryProjectsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTPageURLV2ToSDKPageURLV2,
            paths: [{ path: 'projects.url' }],
          },
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [{ path: 'projects.coverImage.imageInfo' }],
          },
          {
            transformFn: transformRESTVideoV2ToSDKVideoV2,
            paths: [{ path: 'projects.coverVideo.videoInfo' }],
          },
        ])
      );

      return {
        items: transformedData?.projects,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

export interface QueryProjectsOptions {
  /** Whether to include the project's relative path and full URL in the response. Default: `false` */
  includePageUrl?: boolean | null | undefined;
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ProjectsQueryResult extends QueryCursorResult {
  items: Project[];
  query: ProjectsQueryBuilder;
  next: () => Promise<ProjectsQueryResult>;
  prev: () => Promise<ProjectsQueryResult>;
}

export interface ProjectsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'hidden'
      | 'collectionIds'
      | 'slug'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ProjectsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'hidden'
      | 'collectionIds'
      | 'slug'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ProjectsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'slug'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ProjectsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'slug'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ProjectsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'slug'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ProjectsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'slug'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ProjectsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'title' | 'description' | 'slug',
    value: string
  ) => ProjectsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'hidden'
      | 'collectionIds'
      | 'slug'
      | '_createdDate'
      | '_updatedDate',
    value: any[]
  ) => ProjectsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (propertyName: 'collectionIds', value: any[]) => ProjectsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'hidden'
      | 'collectionIds'
      | 'slug'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => ProjectsQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'hidden'
      | 'collectionIds'
      | 'slug'
      | '_createdDate'
      | '_updatedDate',
    value: boolean
  ) => ProjectsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      '_id' | 'title' | 'description' | 'slug' | '_createdDate' | '_updatedDate'
    >
  ) => ProjectsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      '_id' | 'title' | 'description' | 'slug' | '_createdDate' | '_updatedDate'
    >
  ) => ProjectsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => ProjectsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => ProjectsQueryBuilder;
  find: () => Promise<ProjectsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.portfolio.projects.ProjectsService.QueryProjects
 * @requiredField query
 */
export async function typedQueryProjects(
  query: ProjectQuery,
  options?: QueryProjectsOptions
): Promise<
  NonNullablePaths<
    QueryProjectsResponse,
    | `projects`
    | `projects.${number}.coverImage.focalPoint.x`
    | `projects.${number}.coverImage.focalPoint.y`
    | `projects.${number}.seoData.settings.preventAutoRedirect`
    | `projects.${number}.watermark.position`
    | `projects.${number}.watermark.size`
    | `projects.${number}.watermark.opacity`
    | `projects.${number}.watermark.imageUrl`
    | `projects.${number}.watermark.enabled`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts =
    ambassadorWixPortfolioProjectsV1Project.queryProjects(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'projects.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'projects.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'projects.coverVideo.videoInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ProjectQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id'];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: ['description', 'slug', 'title'];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: ['collectionIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'NONE';
    },
    {
      fields: ['hidden'];
      operators: '*';
      sort: 'NONE';
    },
    {
      fields: ['_createdDate', '_updatedDate'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Project,
  ProjectQuerySpec
>;
export type ProjectQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Project, ProjectQuerySpec, ProjectQuery>(),
  },
};

/**
 * Deprecated - please use ProjectsInCollectionsService.UpdateProjectOrderInCollection instead
 * our Client still use it
 * @param sortOrder - Index that determines the placement of a project within the collection.
 * @public
 * @requiredField identifiers
 * @requiredField identifiers.collectionId
 * @requiredField identifiers.projectId
 * @requiredField sortOrder
 * @permissionId PORTFOLIO.PROJECT_UPDATE
 * @applicableIdentity APP
 * @fqn com.wixpress.portfolio.projects.ProjectsService.UpdateProjectOrderInCollection
 * @deprecated
 */
export async function updateProjectOrderInCollection(
  identifiers: NonNullablePaths<
    UpdateProjectOrderInCollectionIdentifiers,
    `collectionId` | `projectId`,
    2
  >,
  sortOrder: number
): Promise<
  NonNullablePaths<
    UpdateProjectOrderInCollectionResponse,
    | `projectInCollection.collectionId`
    | `projectInCollection.project.coverImage.focalPoint.x`
    | `projectInCollection.project.coverImage.focalPoint.y`
    | `projectInCollection.project.collectionIds`
    | `projectInCollection.project.details`
    | `projectInCollection.project.details.${number}.text`
    | `projectInCollection.project.details.${number}.label`
    | `projectInCollection.project.seoData.tags`
    | `projectInCollection.project.seoData.tags.${number}.type`
    | `projectInCollection.project.seoData.tags.${number}.children`
    | `projectInCollection.project.seoData.tags.${number}.custom`
    | `projectInCollection.project.seoData.tags.${number}.disabled`
    | `projectInCollection.project.seoData.settings.preventAutoRedirect`
    | `projectInCollection.project.seoData.settings.keywords`
    | `projectInCollection.project.seoData.settings.keywords.${number}.term`
    | `projectInCollection.project.seoData.settings.keywords.${number}.isMain`
    | `projectInCollection.project.watermark.position`
    | `projectInCollection.project.watermark.size`
    | `projectInCollection.project.watermark.opacity`
    | `projectInCollection.project.watermark.imageUrl`
    | `projectInCollection.project.watermark.enabled`,
    8
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    projectId: identifiers?.projectId,
    collectionId: identifiers?.collectionId,
    sortOrder: sortOrder,
  });

  const reqOpts =
    ambassadorWixPortfolioProjectsV1Project.updateProjectOrderInCollection(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'project.project.url' },
            { path: 'projectInCollection.project.url' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'project.project.coverImage.imageInfo' },
            { path: 'projectInCollection.project.coverImage.imageInfo' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [
            { path: 'project.project.coverVideo.videoInfo' },
            { path: 'projectInCollection.project.coverVideo.videoInfo' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          projectId: '$[0].projectId',
          collectionId: '$[0].collectionId',
          sortOrder: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers', 'sortOrder']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateProjectOrderInCollectionIdentifiers {
  /**
   * ID of the project to update.
   * @format GUID
   */
  projectId: string;
  /**
   * Collection ID.
   * @format GUID
   */
  collectionId: string;
}

/**
 * Deprecated - please use ProjectsInCollectionsService.QueryProjectsInCollections instead
 * our Client still use it
 * @param query - WQL expression
 * @public
 * @requiredField query
 * @permissionId PORTFOLIO.PROJECT_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.portfolio.projects.ProjectsService.QueryProjectsWithCollectionInfo
 * @deprecated
 */
export async function queryProjectsWithCollectionInfo(
  query: QueryV2,
  options?: QueryProjectsWithCollectionInfoOptions
): Promise<
  NonNullablePaths<
    QueryProjectWithCollectionInfoResponse,
    | `projects`
    | `projects.${number}.collectionId`
    | `projects.${number}.project.watermark.position`
    | `projects.${number}.project.watermark.size`
    | `projects.${number}.project.watermark.opacity`
    | `projects.${number}.project.watermark.imageUrl`
    | `projects.${number}.project.watermark.enabled`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    includePageUrl: options?.includePageUrl,
  });

  const reqOpts =
    ambassadorWixPortfolioProjectsV1Project.queryProjectsWithCollectionInfo(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'projects.project.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'projects.project.coverImage.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'projects.project.coverVideo.videoInfo' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          query: '$[0]',
          includePageUrl: '$[1].includePageUrl',
        },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryProjectsWithCollectionInfoOptions {
  /** Include page url */
  includePageUrl?: boolean | null;
}
